const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;

/**
 * Is this resource name already taken?
 *
 * Same route and the same request the antd forms use, so the two agree on what
 * counts as a duplicate: `{ name, id }` in, `{ isDuplicate, message }` back. The
 * `id` excludes the record being edited, or renaming a resource to the name it
 * already has would report itself as a clash.
 *
 * A failed check resolves to "not a duplicate" rather than rejecting. The check
 * is a courtesy — the save that follows is the thing that matters, and a check
 * that cannot reach the server should not be what stops a resource being
 * created.
 *
 * @param {string} name
 * @param {string|null} [id] - the record being renamed, if any.
 * @returns {Promise<{isDuplicate: boolean, message: string}>}
 */
export async function checkResourceDuplicate(name, id = null) {
  try {
    const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/check-resource-duplicate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
      /* `id` is sent only when there is one — a resource being renamed. It tells
         the server to ignore that record when looking for a clash, or keeping a
         resource's existing name would be reported as clashing with itself and
         nothing could ever be saved without renaming it.

         Creating, there is no record yet, so the key is left out entirely rather
         than sent as null. */
      body: JSON.stringify(id ? { name: String(name || "").trim(), id } : { name: String(name || "").trim() }),
    });
    /* The body decides, not the status code. A taken name comes back as
       `success: false` on a non-2xx, so treating "not ok" as "no answer" threw
       the duplicate away and reported the name as free — a tick on a name that
       was already taken. Both outcomes carry `isDuplicate`, so that is what is
       read, whatever the status.

       A reply with no `isDuplicate` in it is a different thing: an expired
       token, a route that is not there, an error page. That is reported as not
       a duplicate — the check is a courtesy and must not block a save — but said
       out loud, because otherwise it is indistinguishable from a free name. */
    const data = await response.json().catch(() => null);

    if (!data || typeof data.isDuplicate !== "boolean") {
      console.warn(
        `Resource name check gave no answer (HTTP ${response.status} ${response.statusText}).`,
        "Response:",
        data,
      );
      return { isDuplicate: false, message: "" };
    }

    return {
      isDuplicate: data.isDuplicate,
      message: data.message || "A resource with this name already exists",
    };
  } catch (error) {
    console.warn("Resource name check could not reach the server:", error.message);
    return { isDuplicate: false, message: "" };
  }
}

/**
 * The names among these that are already taken, each with the server's reason.
 *
 * Checked in one pass before anything is created: finding out that the third of
 * three names is a duplicate after the first two have already been created is
 * worse than not checking at all, since there is no transaction to roll back.
 *
 * A Map rather than a Set so the message travels with the name — the server
 * words it ("A resource with this name already exists") and that is what the
 * user should read, not a second copy of the same sentence written here.
 *
 * @param {Array<{name: string, resourceId: string|null}>} items
 * @returns {Promise<Map<string, string>>} trimmed name → message
 */
export async function findDuplicateNames(items) {
  const results = await Promise.all(
    items.map(async (item) => {
      const name = String(item.name || "").trim();
      if (!name) return null;
      const { isDuplicate, message } = await checkResourceDuplicate(name, item.resourceId);
      return isDuplicate ? [name, message] : null;
    }),
  );
  return new Map(results.filter(Boolean));
}
